import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { Spinner } from "./ui.js";

interface Suggestion {
  id: string;
  priority: "critico" | "alto" | "medio" | "baixo";
  area: string;
  title: string;
  detail: string;
  metric: { value: number; unit: string };
  action: string;
}

const PRIORITY: Record<Suggestion["priority"], { label: string; cls: string }> = {
  critico: { label: "Crítico", cls: "bg-red-100 text-red-700 ring-red-200" },
  alto: { label: "Alto", cls: "bg-orange-100 text-orange-700 ring-orange-200" },
  medio: { label: "Médio", cls: "bg-amber-100 text-amber-800 ring-amber-200" },
  baixo: { label: "Baixo", cls: "bg-slate-100 text-slate-600 ring-slate-200" },
};

// Lista de sugestões de aprimoramento, ranqueada por prioridade (dados ao vivo).
export function Sugestoes() {
  const [open, setOpen] = useState(true);
  const q = useQuery({
    queryKey: ["sugestoes"],
    queryFn: async () => (await api.get("/insights/sugestoes")).data as Suggestion[],
  });
  const items = q.data ?? [];

  return (
    <div className="card p-5" data-testid="sugestoes">
      <button className="flex w-full items-center justify-between" onClick={() => setOpen((v) => !v)}>
        <div className="flex items-center gap-2">
          <span className="text-lg">🎯</span>
          <h2 className="font-semibold text-slate-800">Avisos automáticos</h2>
          <span className="text-xs text-slate-400">(regras)</span>
          {items.length > 0 && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{items.length}</span>
          )}
        </div>
        <span className="text-slate-400">{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-2">
          {q.isLoading && <Spinner />}
          {!q.isLoading && items.length === 0 && (
            <p className="text-sm text-slate-400">Nenhuma sugestão — operação dentro dos parâmetros. 🎉</p>
          )}
          {items.map((s, i) => (
            <div key={s.id} data-testid="sugestao" data-priority={s.priority}
              className="flex gap-3 rounded-lg border border-slate-100 p-3 hover:bg-slate-50">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">{i + 1}</div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`badge ring-1 ${PRIORITY[s.priority].cls}`}>{PRIORITY[s.priority].label}</span>
                  <span className="text-xs text-slate-400">{s.area}</span>
                  <span className="font-medium text-slate-800">{s.title}</span>
                </div>
                <p className="mt-1 text-sm text-slate-500">{s.detail}</p>
                <p className="mt-1 text-sm text-brand-700">→ {s.action}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
